import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import LoadingIcon from "../../../../components/LoadingIcon";

const ManagePOCLimitsModal = ({ open, handleClose }) => {
  const colors = useColors();
  const { setPOCLimits, removePOCLimits, getPOCStatus } = useAPI();

  const [customerId, setCustomerId] = useState("");
  const [totalLimit, setTotalLimit] = useState(10);
  const [dailyLimit, setDailyLimit] = useState(5);
  const [resetCounts, setResetCounts] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusData, setStatusData] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const handleGetStatus = async () => {
    if (!customerId.trim()) {
      setError("Please enter a customer ID");
      return;
    }

    setLoading(true);
    clearMessages();

    try {
      const response = await getPOCStatus(customerId.trim());
      if (response.data.message) {
        setStatusData(null);
        setSuccess(response.data.message);
      } else {
        setStatusData(response.data);
        setSuccess("Status retrieved successfully");
      }
    } catch (err) {
      setError("Failed to get POC limits status");
      setStatusData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSetLimits = async () => {
    if (!customerId.trim()) {
      setError("Please enter a customer ID");
      return;
    }

    if (totalLimit < 1 || dailyLimit < 1) {
      setError("Limits must be greater than 0");
      return;
    }

    setLoading(true);
    clearMessages();

    try {
      await setPOCLimits(
        customerId.trim(),
        totalLimit,
        dailyLimit,
        resetCounts
      );
      setSuccess(
        `POC limits set successfully: Total=${totalLimit}, Daily=${dailyLimit}, Reset=${resetCounts}`
      );
      setStatusData(null); // Clear status to encourage re-checking
    } catch (err) {
      setError("Failed to set POC limits");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveLimits = async () => {
    if (!customerId.trim()) {
      setError("Please enter a customer ID");
      return;
    }

    setLoading(true);
    clearMessages();

    try {
      await removePOCLimits(customerId.trim());
      setSuccess("POC limits removed successfully");
      setStatusData(null);
    } catch (err) {
      setError("Failed to remove POC limits");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setCustomerId("");
    setTotalLimit(10);
    setDailyLimit(5);
    setResetCounts(false);
    setStatusData(null);
    clearMessages();
    handleClose();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleGetStatus();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="md"
      fullWidth
      onKeyUp={handleKeyPress}
    >
      <DialogTitle>
        <Typography variant="h6">Manage POC Limits</Typography>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Customer ID Input */}
          <Grid item xs={12}>
            <TextField
              autoFocus
              label="Customer ID"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              fullWidth
              placeholder="Enter customer ID to manage"
            />
          </Grid>

          {/* Limits Configuration */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Limits Configuration
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Total Limit"
                  type="number"
                  value={totalLimit}
                  onChange={(e) => setTotalLimit(Number(e.target.value))}
                  fullWidth
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Daily Limit"
                  type="number"
                  value={dailyLimit}
                  onChange={(e) => setDailyLimit(Number(e.target.value))}
                  fullWidth
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={resetCounts}
                      onChange={(e) => setResetCounts(e.target.checked)}
                    />
                  }
                  label="Reset current counts (unchecked = update limits while preserving current usage)"
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Button
                variant="outlined"
                onClick={handleGetStatus}
                disabled={loading || !customerId.trim()}
                startIcon={loading ? <LoadingIcon size={16} /> : null}
              >
                Get Status
              </Button>
              <Button
                variant="contained"
                onClick={handleSetLimits}
                disabled={loading || !customerId.trim()}
                startIcon={loading ? <LoadingIcon size={16} /> : null}
                sx={{ backgroundColor: colors.primary_main }}
              >
                Set Limits
              </Button>
              <Button
                variant="outlined"
                onClick={handleRemoveLimits}
                disabled={loading || !customerId.trim()}
                startIcon={loading ? <LoadingIcon size={16} /> : null}
                sx={{
                  color: colors.error_main,
                  borderColor: colors.error_main,
                  "&:hover": {
                    borderColor: colors.error_dark,
                    backgroundColor: colors.error_lighter,
                  },
                }}
              >
                Remove Limits
              </Button>
            </Box>
          </Grid>

          {/* Messages */}
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
          {success && (
            <Grid item xs={12}>
              <Alert severity="success">{success}</Alert>
            </Grid>
          )}

          {/* Status Display */}
          {statusData && (
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: colors.grey_5,
                  borderRadius: 1,
                  border: `1px solid ${colors.grey_20}`,
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Current Status for {customerId}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Total Limit:</strong>{" "}
                      {statusData.total_controls_limit}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Viewed:</strong>{" "}
                      {statusData.total_controls_viewed}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Remaining:</strong>{" "}
                      {statusData.remaining_controls_total}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Daily Limit:</strong>{" "}
                      {statusData.daily_controls_limit}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Viewed Today:</strong>{" "}
                      {statusData.controls_viewed_today}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Remaining Today:</strong>{" "}
                      {statusData.remaining_controls_today}
                    </Typography>
                  </Grid>
                </Grid>
                {statusData.daily_controls_viewed_list &&
                  Object.keys(statusData.daily_controls_viewed_list).length >
                    0 && (
                    <Box mt={2}>
                      <Typography variant="body2">
                        <strong>Recent Daily Views:</strong>
                      </Typography>
                      <Box sx={{ maxHeight: 100, overflow: "auto", mt: 1 }}>
                        {Object.entries(statusData.daily_controls_viewed_list)
                          .slice(-5) // Show last 5 days
                          .map(([date, controls]) => (
                            <Typography
                              key={date}
                              variant="caption"
                              display="block"
                            >
                              {date}:{" "}
                              {Array.isArray(controls) ? controls.length : 0}{" "}
                              controls
                            </Typography>
                          ))}
                      </Box>
                    </Box>
                  )}
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCloseModal}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManagePOCLimitsModal;
